"use client";

import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import type {WorkflowDebugAction} from "@/hooks/useWorkflowTurns";
import type {OmegaRun} from "@/services/wingman/omega-results";
import {traceEventKey} from "@/utils/workflows/workflowRunTraceLabels";
import JsonDisclosure from "./JsonDisclosure";

/** A test run from the conversation, with debug controls while it is paused or running. */
export default function CompiledWorkflowRun({
   run,
   unavailable,
   onDebug,
}: {
   run: OmegaRun;
   unavailable: boolean;
   onDebug: (sessionId: string, action: WorkflowDebugAction) => Promise<boolean>;
}) {
   const {t} = useTranslation("ask");

   return (
      <details className="rounded-lg border border-300 p-3 text-xs" open>
         <summary className="cursor-pointer font-medium text-900">
            {t("workflowPanel.run.title", {status: t(`workflowPanel.run.status.${run.status}`), id: run.id})}
         </summary>
         {run.workflowId && <p className="mt-1 text-500">{t("workflowPanel.run.workflow", {id: run.workflowId})}</p>}
         {run.snapshot != null && (
            <div className="mt-2">
               <JsonDisclosure label={t("workflowPanel.run.debugState")} value={run.snapshot} />
            </div>
         )}
         {run.status === "paused" && (
            <div className="mt-3 space-y-2">
               <p className="text-700">{t("workflowPanel.run.stepWarning")}</p>
               <div className="flex flex-wrap gap-2">
                  {(["step", "run", "stop"] as const).map((action) => (
                     <Button key={action} variant="outline" size="sm" disabled={unavailable} onClick={() => void onDebug(run.id, action)}>
                        {t(`workflowPanel.run.actions.${action}`)}
                     </Button>
                  ))}
               </div>
            </div>
         )}
         {(run.status === "running" || run.status === "stepping") && (
            <Button variant="outline" size="sm" className="mt-3" disabled={unavailable} onClick={() => void onDebug(run.id, "stop")}>
               {t("workflowPanel.run.actions.stop")}
            </Button>
         )}
         {run.traces.map((trace) => (
            <div key={trace.stepIndex} className="mt-2 border-t border-300 pt-2">
               <span className="text-700">
                  {t(trace.isSuccess === false ? "workflowPanel.run.traceFailed" : "workflowPanel.run.trace", {
                     node: trace.nodeName ?? trace.nodeId ?? t("workflowPanel.run.workflowNode"),
                     event: t(`workflowPanel.run.event.${traceEventKey(trace.type)}`),
                  })}
               </span>
               <JsonDisclosure label={t("workflowPanel.run.traceDetails")} value={trace} maxHeight={160} />
            </div>
         ))}
      </details>
   );
}
